import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import API_CONFIG from '../../apiConfig';
import ImageUpload from './ImageUpload';
import WrinkleRemovalSettings from './WrinkleRemovalSettings';
import { examplePresets, ExamplePreset } from './examplePresets';
import { useCreditEstimation } from '../../hooks/useCreditEstimation';
import { useModeRestrictions } from '../../hooks/useModeRestrictions';
import CreditCostPreview from '../ui/CreditCostPreview';
import InsufficientCreditsModal from '../ui/InsufficientCreditsModal';

interface LocationState {
  inputImages: string[];
  recreatingFrom: string;
  generatedImages?: string[];
}

const WrinkleRemoval: React.FC = () => {
  const { currentUser, userCredits, refreshUserCredits } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as LocationState;

  // State to track image URL and results
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [processing, setProcessing] = useState(false);
  const [outputImages, setOutputImages] = useState<string[]>(state?.generatedImages || []);
  const [isExample, setIsExample] = useState(false);
  const [mode, setMode] = useState<string>('balanced');
  const [errorMessage, setErrorMessage] = useState('');
  const [showInsufficientCreditsModal, setShowInsufficientCreditsModal] = useState(false);

  // Credit estimation hook
  const [creditEstimation, estimateCreditCost] = useCreditEstimation('ai-wrinkle-removal');

  // Mode restrictions hook
  const modeRestrictions = useModeRestrictions();

  // Effect to handle recreation state
  useEffect(() => {
    const loadInputImages = async () => {
      if (state?.inputImages && state.inputImages.length > 0) {
        setImageUrls([state.inputImages[0]]);
        if (state.generatedImages) {
          setOutputImages(state.generatedImages);
          setIsExample(false);
        }
      }
    };

    loadInputImages();
  }, [state]);

  // Clear error messages when user uploads new images
  useEffect(() => {
    if (imageUrls.length > 0) {
      setErrorMessage('');
    }
  }, [imageUrls]);

  // Handle mode restrictions
  useEffect(() => {
    if (modeRestrictions.hasInitialLoad && !modeRestrictions.canUseMode(mode)) {
      const availableMode = modeRestrictions.availableModes.find(m => !m.disabled);
      if (availableMode) {
        setMode(availableMode.value);
      }
    }
  }, [modeRestrictions.hasInitialLoad, modeRestrictions.availableModes, mode, modeRestrictions.canUseMode]);

  // Credit estimation effect
  useEffect(() => {
    if (!currentUser?.uid || imageUrls.length === 0) {
      return;
    }

    const estimationPayload = {
      inputImageUrls: imageUrls,
      numImages: 1,
      mode: mode.toLowerCase(),
      quality: mode === 'quality' ? 'high' : mode === 'balanced' ? 'medium' : 'fast',
      userId: currentUser.uid
    };

    estimateCreditCost(estimationPayload);
  }, [
    currentUser?.uid,
    imageUrls,
    mode,
    estimateCreditCost
  ]);

  // Example handling
  const handlePresetSelect = (presetId: number) => {
    const preset = examplePresets.find((p: ExamplePreset) => p.id === presetId);
    if (preset) {
      setOutputImages(preset.results);
      setIsExample(true);
      setImageUrls([preset.preview]);
    }
  };

  const handleRemoveWrinkles = async () => {
    if (imageUrls.length === 0) return;

    if (!creditEstimation.canAfford) {
      setShowInsufficientCreditsModal(true);
      return;
    }

    setProcessing(true);
    setIsExample(false);
    setErrorMessage('');
    try {
      const payload = {
        inputImageUrls: imageUrls,
        numImages: 1,
        userId: currentUser?.uid || 'anonymous',
        mode: mode.toLowerCase(),
        quality: mode === 'quality' ? 'high' : mode === 'balanced' ? 'medium' : 'fast',
      };

      const response = await fetch(API_CONFIG.AI_WRINKLE_REMOVAL_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        setErrorMessage('Something went wrong. Please try again.');
        return;
      }

      const data = await response.json();

      if (data.success && Array.isArray(data.imageUrls) && data.imageUrls.length > 0) {
        setOutputImages(data.imageUrls);
        await refreshUserCredits();
        setErrorMessage('');
      } else {
        setErrorMessage(data.message || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Failed to remove wrinkles:', error);
      setErrorMessage('Failed to remove wrinkles. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="w-full relative">
      {/* Main Content Area - Three Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 p-2">
        {/* First Column - Image Upload */}
        <div className="bg-white rounded-lg shadow-sm border border-accent-taupe flex flex-col">
          <div className="p-4 border-b border-accent-taupe flex-shrink-0">
            <h2 className="text-lg font-semibold text-text-heading">Garment Image</h2>
            <span className="text-sm text-text-description">Upload a garment image to remove wrinkles</span>
          </div>

          <div className="p-4 flex flex-col min-h-[400px]">
            <ImageUpload
              imageUrls={imageUrls}
              setImageUrls={setImageUrls}
              isExample={isExample}
              onExampleSelect={handlePresetSelect}
            />
          </div>
        </div>

        {/* Second Column - Controls */}
        <div className="bg-white rounded-lg shadow-sm border border-accent-taupe flex flex-col">
          <div className="p-4 border-b border-accent-taupe flex-shrink-0">
            <h2 className="text-lg font-semibold text-text-heading">Controls</h2>
            <span className="text-sm text-text-description">Configure your wrinkle removal settings</span>
          </div>

          <div className="p-4 space-y-3 flex-1 overflow-y-auto">
            {/* Header & Clear Button */}
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-text-heading">Wrinkle Removal</label>
                <button
                  onClick={() => {
                    setMode('balanced');
                    setOutputImages([]);
                    setIsExample(false);
                    setErrorMessage('');
                    setImageUrls([]);
                  }}
                  className="text-xs bg-red-100 hover:bg-red-200 text-red-700 px-2 py-1 rounded-md transition-colors"
                  title="Clear all images and settings"
                >
                  Clear
                </button>
              </div>
            </div>

            {/* Settings Panel */}
            <div className="bg-accent-sand rounded-lg p-3">
              <WrinkleRemovalSettings
                mode={mode}
                setMode={setMode}
                modeRestrictions={modeRestrictions}
              />
            </div>

            {/* Credit Cost Preview */}
            <div className="bg-accent-sand rounded-lg p-2">
              <CreditCostPreview
                creditCost={creditEstimation.creditCost}
                isEstimating={creditEstimation.isEstimating}
                error={creditEstimation.error}
                canAfford={creditEstimation.canAfford}
                className="text-xs"
              />
            </div>

            {/* Generate Button */}
            <button
              className={`w-full px-4 py-2.5 rounded-lg font-medium text-sm transition-colors ${
                imageUrls.length > 0
                  ? 'bg-secondary-500 hover:bg-secondary-600 text-white cursor-pointer'
                  : 'bg-gray-300 text-text-description cursor-not-allowed'
              }`}
              onClick={handleRemoveWrinkles}
              disabled={imageUrls.length === 0 || processing}
            >
              {processing ? (
                <div className="flex items-center justify-center space-x-2">
                  <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  <span>Removing Wrinkles...</span>
                </div>
              ) : (
                <>
                  <i className="fas fa-wand-magic-sparkles mr-2"></i>
                  Remove Wrinkles
                </>
              )}
            </button>
            {imageUrls.length === 0 && (
              <p className="text-xs text-text-description text-center">Upload an image to remove wrinkles</p>
            )}

            {/* Error Message */}
            {errorMessage && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-2">
                <div className="flex items-center">
                  <svg className="w-4 h-4 text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-xs text-red-700">{errorMessage}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Third Column - Output Result */}
        <div className="bg-white rounded-lg shadow-sm border border-accent-taupe flex flex-col">
          <div className="p-4 border-b border-accent-taupe flex-shrink-0">
            <h2 className="text-lg font-semibold text-text-heading">Wrinkle-Free Result</h2>
            <span className="text-sm text-text-description">
              {isExample ? 'Example result will appear here' : 'Smooth, wrinkle-free garment result will appear here'}
            </span>
          </div>

          <div className="p-4 flex-1 flex flex-col">
            {processing ? (
              <div className="flex-1 bg-accent-sand rounded-lg border border-accent-taupe flex items-center justify-center min-h-[400px]">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary-500 mx-auto mb-4"></div>
                  <p className="text-sm text-text-description">Removing garment wrinkles...</p>
                  <p className="text-xs text-text-description mt-2">This may take a few moments</p>
                </div>
              </div>
            ) : outputImages.length > 0 ? (
              <div className="relative flex-1 min-h-[400px]">
                <div className="h-[400px] overflow-y-auto">
                  {outputImages.slice(0, 1).map((imageUrl, index) => (
                    <div key={index} className="relative h-full bg-accent-sand rounded-lg border border-accent-taupe overflow-hidden flex items-center justify-center">
                      <img
                        src={imageUrl}
                        alt={`${isExample ? 'Example' : 'Wrinkle-free'} garment`}
                        className="w-full h-full object-contain"
                      />
                      <div className="absolute top-2 right-2 flex space-x-1">
                        {/* Download Button */}
                        <button
                          className="bg-white bg-opacity-90 hover:bg-opacity-100 text-text-heading rounded-full p-1.5 shadow-sm"
                          title="Download"
                          onClick={() => {
                            const link = document.createElement('a');
                            link.href = imageUrl;
                            link.download = `wrinkle-free-garment.jpg`;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                          }}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4" />
                          </svg>
                        </button>
                        {/* Full Screen Button */}
                        <button
                          className="bg-white bg-opacity-90 hover:bg-opacity-100 text-text-heading rounded-full p-1.5 shadow-sm"
                          title="Open Fullscreen"
                          onClick={() => window.open(imageUrl, '_blank')}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 3H5a2 2 0 00-2 2v3m0 8v3a2 2 0 002 2h3m8-16h3a2 2 0 012 2v3m0 8v3a2 2 0 01-2 2h-3" />
                          </svg>
                        </button>
                        {/* Upscale Button */}
                        <button
                          className="bg-white bg-opacity-90 hover:bg-opacity-100 text-text-heading rounded-full p-1.5 shadow-sm"
                          title="Upscale"
                          onClick={() => {
                            navigate('/canvas-editor', {
                              state: {
                                inputImages: [imageUrl],
                                tool: 'upscaler'
                              }
                            });
                          }}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 12h16M12 4v16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {isExample && (
                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start">
                      <i className="fas fa-info-circle text-blue-600 mr-2 mt-0.5"></i>
                      <div>
                        <p className="text-xs text-blue-800 font-medium">Example Result</p>
                        <p className="text-xs text-blue-700 mt-1">
                          This is an example result. Upload your own wrinkled garment image to remove wrinkles.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex-1 bg-accent-sand rounded-lg border border-accent-taupe flex items-center justify-center min-h-[400px]">
                <div className="text-center text-text-description">
                  <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p className="text-sm">Upload a garment image and click Remove Wrinkles to see result</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Insufficient Credits Modal */}
      <InsufficientCreditsModal
        isOpen={showInsufficientCreditsModal}
        onClose={() => setShowInsufficientCreditsModal(false)}
        requiredCredits={creditEstimation.creditCost || 0}
        availableCredits={userCredits}
        featureName="Wrinkle Removal"
      />
    </div>
  );
};

export default WrinkleRemoval;
